import { useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import gsap from "gsap";

interface FlipCardProps {
  frontContent: ReactNode;
  backContent: ReactNode;
  className?: string;
  duration?: number;
}

export const FlipCard = ({
  frontContent,
  backContent,
  className,
  duration = 0.6,
}: FlipCardProps) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<gsap.core.Tween | null>(null);

  const handleFlip = () => {
    if (!cardRef.current) return;

    if (animationRef.current) {
      animationRef.current.kill();
    }

    const newFlipped = !isFlipped;

    animationRef.current = gsap.to(cardRef.current, {
      rotateY: newFlipped ? 180 : 0,
      duration,
      ease: "power2.inOut",
      onComplete: () => {
        setIsFlipped(newFlipped);
      },
    });
  };

  return (
    <div
      className={cn("h-[268px] w-full cursor-pointer", className)}
      style={{ perspective: "1000px" }}
      onMouseEnter={() => !isFlipped && handleFlip()}
      onMouseLeave={() => isFlipped && handleFlip()}
    >
      <div
        ref={cardRef}
        className="relative h-full w-full"
        style={{ transformStyle: "preserve-3d", willChange: "transform" }}
      >
        <div
          className="absolute inset-0 h-full w-full overflow-hidden rounded-3xl shadow-[0px_2px_20px_3px_#37322B1F]"
          style={{ backfaceVisibility: "hidden" }}
        >
          {frontContent}
        </div>
        <div
          className="absolute inset-0 h-full w-full overflow-hidden rounded-3xl shadow-[0px_2px_20px_3px_#37322B1F]"
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          {backContent}
        </div>
      </div>
    </div>
  );
};

interface FlipCardBadgeProps {
  children: ReactNode;
  variant?: "problem" | "solution";
}

export const FlipCardBadge = ({
  children,
  variant = "problem",
}: FlipCardBadgeProps) => {
  return (
    <div className="relative inline-block">
      <div
        className="absolute inset-0 rounded-full opacity-55 blur-3xl"
        style={{
          background: "radial-gradient(circle, #B91010 0%, transparent 100%)",
          transform: "scale(2)",
        }}
      />
      <span
        className={cn(
          "text-md relative items-center rounded-full border-2 px-6 py-2 leading-none font-medium",
          variant === "problem" &&
            "text-flip-problem border-flip-problem bg-red-100",
          variant === "solution" && "border-white/40 bg-white/20 text-white",
        )}
      >
        {children}
      </span>
    </div>
  );
};

interface FlipCardContentProps {
  badge?: ReactNode;
  title: string;
  description: string;
  footer?: ReactNode;
  variant?: "front" | "back";
}

export const FlipCardContent = ({
  badge,
  title,
  description,
  footer,
  variant = "front",
}: FlipCardContentProps) => {
  return (
    <div
      className={cn(
        "flex h-full w-full flex-col overflow-hidden p-8",
        variant === "front" && "text-flip-front-foreground bg-white",
        variant === "back" && "bg-flip-back text-flip-back-foreground",
      )}
    >
      {badge && <div className="mt-2 mb-6 flex-shrink-0">{badge}</div>}
      <h3
        className={cn(
          "mb-3 flex-shrink-0 text-2xl leading-tight font-bold",
          variant === "front" && "text-foreground",
          variant === "back" && "text-white",
        )}
      >
        {title}
      </h3>
      <p
        className={cn(
          "flex-grow overflow-hidden text-base",
          variant === "front" && "text-muted-foreground",
          variant === "back" && "text-white/90",
        )}
      >
        {description}
      </p>
      {footer && (
        <div
          className={cn(
            "mt-4 flex flex-shrink-0 items-center gap-2 text-sm",
            variant === "front" && "text-muted-foreground/70",
            variant === "back" && "text-white/80",
          )}
        >
          {footer}
        </div>
      )}
    </div>
  );
};
